import React, { useState, useEffect } from 'react';
import NavigationBar from '../NavigationBar';
import axios from '../axios';
import { Container, Row, Col, Table, Button, Form, Badge, Tabs, Tab, Card, ListGroup } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import EditPurchaseModal from './EditPurchaseModal';

const LOCAL_STAGES = ['draft', 'poSent', 'confirmed', 'received', 'closed'];
const IMPORT_STAGES = ['draft', 'poSent', 'piReceived', 'piApproved', 'lcRequested', 'lcOpened', 'production', 'shipped', 'received', 'closed'];
const STAGE_LABELS = {
    draft: 'Draft', poSent: 'PO Sent', confirmed: 'Confirmed', piReceived: 'PI Received', piApproved: 'PI Approved',
    lcRequested: 'LC Requested', lcOpened: 'LC Opened', production: 'In Production', shipped: 'Shipped',
    received: 'Received', closed: 'Closed',
};
const DOC_TYPES = [
    'PO', 'PI', 'LC Request Letter', 'Draft LC', 'Ack LC', 'Insurance Cover Note', 'Commercial Invoice',
    'Packing List', 'Weight List', 'Bill of Lading', 'Certificate of Origin', 'Retirement Letter',
    'C&F Handover Letter', 'Bill of Entry', 'Other',
];

function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

function PurchaseFile() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);

    const [docForm, setDocForm] = useState({ docType: 'Other', isOriginal: false, receivedVia: '', receivedFrom: '', receivedDate: '', note: '', file: null });
    const [noteForm, setNoteForm] = useState({ kind: 'Note', channel: '', counterparty: '', text: '', nextFollowUpDate: '' });
    const [receiveRows, setReceiveRows] = useState([]);
    const [holdReason, setHoldReason] = useState('');
    const [showEdit, setShowEdit] = useState(false);

    useEffect(() => { fetchDetail(); }, [id]);

    const fetchDetail = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${window.backendUrl}/purchases/${id}`);
            setDetail(res.data);
            const lines = res.data.lines || [];
            setReceiveRows(lines.map(l => ({
                lineId: l.LineID, gsmId: l.GSMID, materialId: l.MaterialID,
                label: l.GSMID ? `${l.GSMName} ${l.GSMValue}${l.WidthInch ? ' - ' + l.WidthInch + '"' : ''}` : l.MaterialName,
                remaining: Math.max(0, (parseFloat(l.Qty) || 0) - (parseFloat(l.ReceivedQty) || 0)),
                rollSize: l.WidthInch || '', qty: Math.max(0, (parseFloat(l.Qty) || 0) - (parseFloat(l.ReceivedQty) || 0)),
                rate: l.UnitPrice || '',
            })));
        } catch (err) {
            console.error('Error fetching purchase:', err);
        } finally {
            setLoading(false);
        }
    };

    if (loading || !detail) {
        return (<><NavigationBar /><Container fluid style={{ marginTop: 20 }}><p>Loading...</p></Container></>);
    }

    const { purchase, lines, documents, activity } = detail;
    const isImport = !!purchase.IsForeign || purchase.PurchaseType === 'Import';
    const stageList = isImport ? IMPORT_STAGES : LOCAL_STAGES;
    const isPaper = purchase.PurchaseCategory === 'Direct';
    // Paper comes in through Receive Rolls (many trucks per PO); other materials still use the receive table below
    const canReceive = isPaper
        ? purchase.Stage !== 'closed' && !purchase.IsCancelled
        : purchase.Stage !== 'received' && purchase.Stage !== 'closed' && !purchase.IsCancelled;
    const receipts = detail.receipts || [];
    const canEdit = !purchase.IsCancelled && purchase.Stage !== 'closed';
    const hasReceived = receipts.length > 0 || lines.some(l => parseFloat(l.ReceivedQty) > 0);
    const generatedPo = documents.find(d => d.DocType === 'PO' && d.Source === 'Generated');
    const uploadedDocs = documents.filter(d => d.Source !== 'Generated');
    const transportCost = parseFloat(purchase.TransportCost) || 0;

    const changeStage = async (stage) => {
        try {
            await axios.put(`${window.backendUrl}/purchases/${id}/stage`, { stage });
            fetchDetail();
        } catch (err) { console.error(err); alert('Failed to change stage'); }
    };

    const toggleHold = async (onHold) => {
        try {
            await axios.put(`${window.backendUrl}/purchases/${id}/stage`, { onHold, holdCancelReason: holdReason || undefined });
            fetchDetail();
        } catch (err) { console.error(err); }
    };

    const cancelPurchase = async (detachReceipts = false) => {
        if (!detachReceipts && !window.confirm('Cancel this purchase file? This cannot be undone.')) return;
        try {
            await axios.put(`${window.backendUrl}/purchases/${id}/stage`, { isCancelled: true, holdCancelReason: holdReason || undefined, detachReceipts });
            fetchDetail();
        } catch (err) {
            // Rolls already received on this PO: they stay in stock as "No PO" if the user agrees
            if (err?.response?.status === 409 && window.confirm(err.response.data.error)) return cancelPurchase(true);
            console.error(err);
        }
    };

    const handleDocUpload = async () => {
        if (!docForm.docType) return;
        try {
            let fileBase64 = null, fileName = null;
            if (docForm.file) { fileBase64 = await fileToBase64(docForm.file); fileName = docForm.file.name; }
            await axios.post(`${window.backendUrl}/purchases/${id}/documents`, {
                docType: docForm.docType, isOriginal: docForm.isOriginal, fileBase64, fileName,
                receivedVia: docForm.receivedVia || null, receivedFrom: docForm.receivedFrom || null,
                receivedDate: docForm.receivedDate || null, note: docForm.note || null,
            });
            setDocForm({ docType: 'Other', isOriginal: false, receivedVia: '', receivedFrom: '', receivedDate: '', note: '', file: null });
            fetchDetail();
        } catch (err) { console.error(err); alert('Failed to log document'); }
    };

    const handleAddNote = async () => {
        if (!noteForm.text) return;
        try {
            await axios.post(`${window.backendUrl}/purchases/${id}/activity`, noteForm);
            setNoteForm({ kind: 'Note', channel: '', counterparty: '', text: '', nextFollowUpDate: '' });
            fetchDetail();
        } catch (err) { console.error(err); alert('Failed to save'); }
    };

    const markFollowUpDone = async (activityId) => {
        try {
            await axios.put(`${window.backendUrl}/purchase_activity/${activityId}/done`);
            fetchDetail();
        } catch (err) { console.error(err); }
    };

    const updateReceiveRow = (idx, field, value) => {
        const next = [...receiveRows];
        next[idx] = { ...next[idx], [field]: value };
        setReceiveRows(next);
    };

    const handleReceive = async () => {
        const activeRows = receiveRows.filter(r => parseFloat(r.qty) > 0);
        if (activeRows.length === 0) { alert('Enter a quantity for at least one line'); return; }
        const payload = purchase.PurchaseCategory === 'Direct'
            ? { directRolls: activeRows.map(r => ({ lineId: r.lineId, gsmId: r.gsmId, rollSize: r.rollSize, qty: r.qty, rate: r.rate })) }
            : { indirectLines: activeRows.map(r => ({ lineId: r.lineId, materialId: r.materialId, qty: r.qty, rate: r.rate })) };
        try {
            await axios.post(`${window.backendUrl}/purchases/${id}/receive`, payload);
            alert('Goods received and stock updated.');
            fetchDetail();
        } catch (err) { console.error(err); alert('Failed to receive goods'); }
    };

    return (
        <>
            <NavigationBar />
            <Container fluid style={{ marginTop: 20, marginBottom: 40 }}>
                <Row className="align-items-center mb-2">
                    <Col xs="auto"><h3 className="mb-0">{purchase.PONo}</h3></Col>
                    <Col xs="auto"><Badge bg="info">{purchase.PurchaseCategory}</Badge></Col>
                    <Col xs="auto"><Badge bg="secondary">{purchase.PurchaseType}</Badge></Col>
                    <Col xs="auto"><Badge bg={purchase.IsCancelled ? 'dark' : purchase.OnHold ? 'warning' : 'primary'} text={purchase.OnHold && !purchase.IsCancelled ? 'dark' : undefined}>
                        {purchase.IsCancelled ? 'Cancelled' : purchase.OnHold ? 'On hold' : STAGE_LABELS[purchase.Stage] || purchase.Stage}
                    </Badge></Col>
                    <Col className="text-end" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        {canEdit && <Button size="sm" variant="outline-primary" onClick={() => setShowEdit(true)}>Edit</Button>}
                        <Button size="sm" variant="outline-dark" onClick={() => window.open(`/purchase-order-print/${id}`, '_blank')}>Print PO</Button>
                        <Button size="sm" variant="outline-secondary" onClick={() => navigate('/purchase-board')}>Back to Board</Button>
                    </Col>
                </Row>
                <Row className="mb-3 text-muted">
                    <Col xs="auto">Vendor: <strong>{purchase.VendorName}</strong></Col>
                    <Col xs="auto">PO Date: {String(purchase.PODate).slice(0, 10)}</Col>
                    <Col xs="auto">Expected arrival: {purchase.ExpectedArrivalDate ? String(purchase.ExpectedArrivalDate).slice(0, 10) : '-'}</Col>
                    <Col xs="auto">Total: {purchase.Currency} {Number(purchase.TotalAmount).toLocaleString()}</Col>
                    {transportCost > 0 && <Col xs="auto">Transport cost: {purchase.Currency} {transportCost.toLocaleString()}</Col>}
                </Row>

                {!purchase.IsCancelled && (
                    <Row className="mb-3 align-items-center">
                        <Col xs="auto">
                            <Form.Select size="sm" value={purchase.Stage} onChange={e => changeStage(e.target.value)} style={{ width: 180 }}>
                                {stageList.map(s => <option key={s} value={s}>{STAGE_LABELS[s] || s}</option>)}
                            </Form.Select>
                        </Col>
                        <Col xs="auto">
                            <Form.Control size="sm" placeholder="Reason (for hold/cancel)" value={holdReason} onChange={e => setHoldReason(e.target.value)} style={{ width: 220 }} />
                        </Col>
                        <Col xs="auto">
                            {purchase.OnHold ? (
                                <Button size="sm" variant="outline-success" onClick={() => toggleHold(false)}>Resume</Button>
                            ) : (
                                <Button size="sm" variant="outline-warning" onClick={() => toggleHold(true)}>Put on hold</Button>
                            )}
                        </Col>
                        <Col xs="auto"><Button size="sm" variant="outline-danger" onClick={() => cancelPurchase()}>Cancel purchase</Button></Col>
                    </Row>
                )}

                <Tabs defaultActiveKey="summary" className="mb-3">
                    <Tab eventKey="summary" title="Summary">
                        <Row>
                            <Col md={6}>
                                <Card body className="mb-3">
                                    <h6>Vendor</h6>
                                    <p className="mb-1">{purchase.VendorName} ({purchase.Country})</p>
                                    <p className="mb-1">{purchase.ContactPerson} — {purchase.Phone || purchase.WhatsApp || '-'}</p>
                                    {purchase.AgentName && <p className="mb-1">Agent: {purchase.AgentName} ({purchase.AgentContact})</p>}
                                    {purchase.LeadTimeDays && <p className="mb-0">Lead time: {purchase.LeadTimeDays} days</p>}
                                </Card>
                            </Col>
                            <Col md={6}>
                                <Card body className="mb-3">
                                    <h6>Terms</h6>
                                    <p className="mb-1">Incoterm: {purchase.Incoterm || '-'}</p>
                                    <p className="mb-1">Port of loading / discharge: {purchase.PortOfLoading || '-'} / {purchase.PortOfDischarge || '-'}</p>
                                    <p className="mb-0">Payment term: {purchase.PaymentTerm || '-'}</p>
                                    {transportCost > 0 && <p className="mb-0 mt-1">Transport cost: {purchase.Currency} {transportCost.toLocaleString()}</p>}
                                </Card>
                            </Col>
                        </Row>
                        {purchase.Remarks && <Card body><h6>Remarks</h6><p className="mb-0">{purchase.Remarks}</p></Card>}
                        {isImport && (
                            <Card body className="mt-3 bg-light">
                                <p className="mb-0 text-muted">Bank &amp; LC, Shipping, Payment and Clearance for import purchases are coming in the next phase of this module. For now, log documents and follow-ups in the tabs above and record the LC/shipment details as notes.</p>
                            </Card>
                        )}
                    </Tab>

                    <Tab eventKey="order" title="Purchase Order">
                        <Table size="sm" bordered striped>
                            <thead>
                                <tr>
                                    <th>#</th><th>Item</th><th>Qty</th><th>Unit</th><th>Unit price</th><th>Amount</th><th>Received</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lines.map(l => (
                                    <tr key={l.LineID}>
                                        <td>{l.LineNo}</td>
                                        <td>{l.GSMID ? `${l.GSMName} ${l.GSMValue}${l.WidthInch ? ' - ' + l.WidthInch + '"' : ''}` : (l.MaterialName || l.Description)}</td>
                                        <td>{l.Qty}</td>
                                        <td>{l.Unit}</td>
                                        <td>{l.UnitPrice}</td>
                                        <td>{Number(l.Amount).toLocaleString()}</td>
                                        <td>{l.ReceivedQty}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>

                        {isPaper && (
                            <>
                                <hr />
                                <div className="d-flex align-items-center mb-2" style={{ gap: 12 }}>
                                    <h6 className="mb-0">Rolls received ({receipts.length} {receipts.length === 1 ? 'truck' : 'trucks'})</h6>
                                    {canReceive && <Button size="sm" variant="success" onClick={() => navigate(`/roll-purchase?purchaseId=${id}`)}>Receive rolls for this PO</Button>}
                                </div>
                                {receipts.length === 0 ? <p className="text-muted">Nothing received yet.</p> : (
                                    <Table size="sm" bordered hover>
                                        <thead><tr><th>Receipt</th><th>Date</th><th>Challan</th><th>Truck</th><th>Rolls</th><th>Kg</th></tr></thead>
                                        <tbody>
                                            {receipts.map(r => (
                                                <tr key={r.ReceiptID} style={{ cursor: 'pointer' }} onClick={() => navigate(`/roll-purchase?receiptId=${r.ReceiptID}`)}>
                                                    <td>{r.ReceiptNo}</td><td>{r.ReceivedDateText}</td><td>{r.ChallanNo || ''}</td><td>{r.TruckNo || ''}</td>
                                                    <td>{r.Rolls}</td><td>{Number(r.Kg).toLocaleString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                )}
                            </>
                        )}

                        {canReceive && !isPaper && (
                            <>
                                <hr />
                                <h6>Receive Goods</h6>
                                <p className="text-muted">Materials are added to the material stock ledger.</p>
                                <Table size="sm" bordered>
                                    <thead>
                                        <tr>
                                            <th>Item</th>
                                            {purchase.PurchaseCategory === 'Direct' && <th>Roll size (inch)</th>}
                                            <th>Qty received</th><th>Rate</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {receiveRows.map((r, idx) => (
                                            <tr key={r.lineId}>
                                                <td>{r.label}</td>
                                                {purchase.PurchaseCategory === 'Direct' && (
                                                    <td><Form.Control size="sm" value={r.rollSize} onChange={e => updateReceiveRow(idx, 'rollSize', e.target.value)} /></td>
                                                )}
                                                <td><Form.Control size="sm" type="number" value={r.qty} onChange={e => updateReceiveRow(idx, 'qty', e.target.value)} /></td>
                                                <td><Form.Control size="sm" type="number" value={r.rate} onChange={e => updateReceiveRow(idx, 'rate', e.target.value)} /></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                                <Button variant="success" onClick={handleReceive}>Confirm Received</Button>
                            </>
                        )}
                    </Tab>

                    <Tab eventKey="documents" title="Documents">
                        <Card body className="mb-3">
                            <Row>
                                <Col md={3}>
                                    <Form.Label>Document type</Form.Label>
                                    <Form.Select value={docForm.docType} onChange={e => setDocForm({ ...docForm, docType: e.target.value })}>
                                        {DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                    </Form.Select>
                                </Col>
                                <Col md={3}>
                                    <Form.Label>Received via</Form.Label>
                                    <Form.Control value={docForm.receivedVia} onChange={e => setDocForm({ ...docForm, receivedVia: e.target.value })} placeholder="Email / WhatsApp / Hand" />
                                </Col>
                                <Col md={3}>
                                    <Form.Label>From</Form.Label>
                                    <Form.Control value={docForm.receivedFrom} onChange={e => setDocForm({ ...docForm, receivedFrom: e.target.value })} />
                                </Col>
                                <Col md={3}>
                                    <Form.Label>Date</Form.Label>
                                    <Form.Control type="date" value={docForm.receivedDate} onChange={e => setDocForm({ ...docForm, receivedDate: e.target.value })} />
                                </Col>
                            </Row>
                            <Row className="mt-2">
                                <Col md={6}>
                                    <Form.Label>File (optional)</Form.Label>
                                    <Form.Control type="file" onChange={e => setDocForm({ ...docForm, file: e.target.files[0] })} />
                                </Col>
                                <Col md={4} className="d-flex align-items-end">
                                    <Form.Check type="checkbox" label="This is the original / signed copy" checked={docForm.isOriginal} onChange={e => setDocForm({ ...docForm, isOriginal: e.target.checked })} />
                                </Col>
                                <Col md={2} className="d-flex align-items-end">
                                    <Button onClick={handleDocUpload}>Log Document</Button>
                                </Col>
                            </Row>
                        </Card>
                        <Table size="sm" bordered striped>
                            <thead><tr><th>Type</th><th>Original?</th><th>Received via</th><th>From</th><th>Date</th><th>File</th></tr></thead>
                            <tbody>
                                {/* The PO made by the system is always listed first; it opens in the PO page to edit, save and print */}
                                <tr style={{ backgroundColor: '#eef5ff' }}>
                                    <td><strong>PO</strong> <Badge bg="info">Generated</Badge></td>
                                    <td>{generatedPo ? <Badge bg="success">Saved</Badge> : <Badge bg="secondary">Not saved yet</Badge>}</td>
                                    <td>-</td>
                                    <td>{purchase.PONo}</td>
                                    <td>{generatedPo ? String(generatedPo.CreatedAt).slice(0, 10) : '-'}</td>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        <Button size="sm" variant="outline-primary" className="me-2" onClick={() => window.open(`/purchase-order-print/${id}`, '_blank')}>Open / Edit PO</Button>
                                    </td>
                                </tr>
                                {uploadedDocs.length === 0 ? (
                                    <tr><td colSpan={6} className="text-center text-muted">No other documents logged yet.</td></tr>
                                ) : uploadedDocs.map(d => (
                                    <tr key={d.DocID}>
                                        <td>{d.DocType}</td>
                                        <td>{d.IsOriginal ? <Badge bg="success">Original</Badge> : <Badge bg="secondary">Copy</Badge>}</td>
                                        <td>{d.ReceivedVia || '-'}</td>
                                        <td>{d.ReceivedFrom || '-'}</td>
                                        <td>{d.ReceivedDate ? String(d.ReceivedDate).slice(0, 10) : '-'}</td>
                                        <td>{d.FileUrl ? <a href={d.FileUrl} target="_blank" rel="noreferrer">View</a> : '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Tab>

                    <Tab eventKey="comms" title="Communication Log">
                        <Card body className="mb-3">
                            <Row>
                                <Col md={2}>
                                    <Form.Select value={noteForm.kind} onChange={e => setNoteForm({ ...noteForm, kind: e.target.value })}>
                                        <option value="Note">Note</option>
                                        <option value="Sent">Sent to vendor</option>
                                        <option value="Received">Received from vendor</option>
                                        <option value="Follow-up">Follow-up needed</option>
                                    </Form.Select>
                                </Col>
                                <Col md={2}>
                                    <Form.Control placeholder="Channel" value={noteForm.channel} onChange={e => setNoteForm({ ...noteForm, channel: e.target.value })} />
                                </Col>
                                <Col md={2}>
                                    <Form.Control placeholder="Who" value={noteForm.counterparty} onChange={e => setNoteForm({ ...noteForm, counterparty: e.target.value })} />
                                </Col>
                                <Col md={4}>
                                    <Form.Control placeholder="What happened..." value={noteForm.text} onChange={e => setNoteForm({ ...noteForm, text: e.target.value })} />
                                </Col>
                                <Col md={2}>
                                    <Form.Control type="date" placeholder="Next follow-up" value={noteForm.nextFollowUpDate} onChange={e => setNoteForm({ ...noteForm, nextFollowUpDate: e.target.value })} />
                                </Col>
                            </Row>
                            <div className="mt-2 text-end"><Button size="sm" onClick={handleAddNote}>Add</Button></div>
                        </Card>
                        <ListGroup>
                            {activity.length === 0 ? (
                                <ListGroup.Item className="text-muted text-center">No activity yet.</ListGroup.Item>
                            ) : activity.map(a => (
                                <ListGroup.Item key={a.ActivityID} className="d-flex justify-content-between align-items-start">
                                    <div>
                                        <Badge bg="light" text="dark" className="me-2">{a.Kind}</Badge>
                                        {a.Text}
                                        {a.Counterparty && <span className="text-muted"> — {a.Counterparty}</span>}
                                        {a.NextFollowUpDate && !a.FollowUpDone && (
                                            <div className="text-warning small">Follow up by {String(a.NextFollowUpDate).slice(0, 10)}</div>
                                        )}
                                    </div>
                                    <div className="text-end">
                                        <div className="text-muted small">{String(a.CreatedAt).slice(0, 16)}</div>
                                        {a.NextFollowUpDate && !a.FollowUpDone && (
                                            <Button size="sm" variant="link" onClick={() => markFollowUpDone(a.ActivityID)}>Mark done</Button>
                                        )}
                                    </div>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    </Tab>
                </Tabs>
            </Container>

            {canEdit && (
                <EditPurchaseModal
                    show={showEdit}
                    onHide={() => setShowEdit(false)}
                    purchase={purchase}
                    lines={lines}
                    hasReceived={hasReceived}
                    onSaved={() => { setShowEdit(false); fetchDetail(); }}
                />
            )}
        </>
    );
}

export default PurchaseFile;
